import { Component, NgZone, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { fadeAnimation } from './animations';

import { UserIdleService } from 'angular-user-idle';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { UserIdleModalComponent } from './user-idle-modal.component';
import { TokenExpiryModalComponent } from './token-expiry-modal.component';
import { AuthenticationService, SocketService, SessionTabSyncService } from './_services';
import { persistGotoForLogin } from './_utils/helpers';
import { USER_IDLE_CONFIG, TOKEN_EXPIRY_WARN_MS, TOKEN_EXPIRY_CHECK_INTERVAL_MS } from './app.constants';
import { environment } from 'environments/environment';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  animations: [fadeAnimation]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'cadmin';
  bsUserIdleModalRef: BsModalRef | null = null;
  bsTokenExpiryModalRef: BsModalRef | null = null;
  private tokenExpiryCheckHandle: any = null;
  private tokenExpiryWarned: boolean = false;
  private refreshingOnReturn: boolean = false;
  private readonly onLocalActivity = () => this.sessionTabSync.notifyActivity();
  private readonly onVisibilityChange = () => this.handleTabVisible();

  constructor(
    private authenticationService: AuthenticationService,
    private userIdleService: UserIdleService,
    private modalService: BsModalService,
    private socketService: SocketService,
    private sessionTabSync: SessionTabSyncService,
    private ngZone: NgZone,
    private router: Router,
  ) { }

  private dismissSessionModals() {
    if (this.bsTokenExpiryModalRef) {
      this.bsTokenExpiryModalRef.hide();
      this.bsTokenExpiryModalRef = null;
    }
    if (this.bsUserIdleModalRef) {
      this.bsUserIdleModalRef.hide();
      this.bsUserIdleModalRef = null;
    }
    this.userIdleService.stopTimer();
  }

  ngOnDestroy() {
    if (this.tokenExpiryCheckHandle) {
      clearInterval(this.tokenExpiryCheckHandle);
    }
    if (typeof window !== 'undefined') {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, this.onLocalActivity, true));
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  /**
   * When the user refocuses a tab that sat in the background, browser timer
   * throttling means the periodic token-expiry watcher may not have run. Re-check
   * the JWT immediately: refresh it if it is still valid but near expiry (so the
   * user is not bounced after clicking "stay logged in"), or log out cleanly with
   * a clear message if it already expired (an expired token cannot be refreshed).
   */
  private handleTabVisible() {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
      return;
    }
    if (!this.authenticationService.id()) {
      return;
    }
    if (this.bsTokenExpiryModalRef || this.bsUserIdleModalRef) {
      return;
    }
    const expMs = this.authenticationService.tokenExpiresAtMs();
    if (!expMs) {
      return;
    }
    const remaining = expMs - Date.now();
    if (remaining <= 0) {
      this.handleExpiredOnReturn();
      return;
    }
    if (remaining <= TOKEN_EXPIRY_WARN_MS && !this.refreshingOnReturn) {
      this.refreshingOnReturn = true;
      this.authenticationService.refreshToken().subscribe(
        () => {
          this.refreshingOnReturn = false;
          this.tokenExpiryWarned = false;
          this.sessionTabSync.notifySessionExtended();
        },
        () => {
          this.refreshingOnReturn = false;
          this.handleExpiredOnReturn();
        },
      );
    }
  }

  private handleExpiredOnReturn() {
    this.performSessionLogout('expired');
  }

  /** Log out and redirect to login, unless already on a public auth route. */
  private performSessionLogout(sessionQuery?: string) {
    this.dismissSessionModals();
    this.sessionTabSync.dismissModalsLocally();
    const returnUrl = this.router.url;
    if (returnUrl.split('?')[0] === '/login') {
      return;
    }
    if (!this.authenticationService.id()) {
      if (!this.isPublicAuthRoute()) {
        this.router.navigate(['login'], {
          queryParams: sessionQuery
            ? { ...persistGotoForLogin(returnUrl), session: sessionQuery }
            : persistGotoForLogin(returnUrl),
        });
      }
      return;
    }
    this.authenticationService.logout();
    this.userIdleService.resetTimer();
    this.router.navigate(['login'], {
      queryParams: sessionQuery
        ? { ...persistGotoForLogin(returnUrl), session: sessionQuery }
        : persistGotoForLogin(returnUrl),
    });
  }

  private isPublicAuthRoute(): boolean {
    const path = this.router.url.split('?')[0];
    return ['/login', '/forgot-password', '/reset-password'].includes(path);
  }

  private startTokenExpiryWatcher() {
    if (this.tokenExpiryCheckHandle) {
      clearInterval(this.tokenExpiryCheckHandle);
    }
    this.tokenExpiryCheckHandle = setInterval(() => {
      const expMs = this.authenticationService.tokenExpiresAtMs();
      if (!expMs) {
        this.tokenExpiryWarned = false;
        return;
      }
      const remaining = expMs - Date.now();
      if (remaining > 0 && remaining <= TOKEN_EXPIRY_WARN_MS && !this.tokenExpiryWarned) {
        // Reserve immediately so the 30s interval doesn't re-enter while electing.
        this.tokenExpiryWarned = true;
        this.sessionTabSync.requestWarning('token_expiry').then((shouldShow) => {
          if (!shouldShow || !this.authenticationService.isSessionValid()) {
            return;
          }
          const remainingNow = (this.authenticationService.tokenExpiresAtMs() || 0) - Date.now();
          if (remainingNow <= 0) {
            return;
          }
          this.bsTokenExpiryModalRef = this.modalService.show(TokenExpiryModalComponent, {
            initialState: { secondsRemaining: Math.floor(remainingNow / 1000) },
            class: 'modal-sm',
            backdrop: 'static',
            keyboard: false,
          });
          this.bsTokenExpiryModalRef.content.onExtended.subscribe(() => {
            this.tokenExpiryWarned = false;
          });
        });
      } else if (remaining > TOKEN_EXPIRY_WARN_MS) {
        // token was refreshed; allow re-warning on next near-expiry
        this.tokenExpiryWarned = false;
      }
    }, TOKEN_EXPIRY_CHECK_INTERVAL_MS);
  }

  /**
   * Loads Google Analytics (gtag.js) and, when a recipient opens a personalized
   * invite link such as ?ref=jwarren@barkani.com (or ?email=...), records a
   * single GA event identifying who visited. Fires once per visitor session so a
   * page refresh does not inflate the count. No-op when no measurement ID is set
   * (e.g. local/dev builds) or when GA has already been loaded.
   */
  private initAnalytics() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    const measurementId = (environment as any).GOOGLE_ANALYTICS_MEASUREMENT_ID;
    if (!measurementId || measurementId === 'G-XXXXXXXX') {
      return;
    }
    const w = window as any;
    try {
      if (!w.dataLayer) {
        w.dataLayer = w.dataLayer || [];
        w.gtag = function () { w.dataLayer.push(arguments); };
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        document.head.appendChild(script);
        w.gtag('js', new Date());
        w.gtag('config', measurementId);
      }

      const params = new URLSearchParams(window.location.search);
      const ref = (params.get('ref') || params.get('email') || '').trim();
      if (!ref) {
        return;
      }
      const seenKey = `barkani_invite_ref_seen:${ref}`;
      if (sessionStorage.getItem(seenKey)) {
        return;
      }
      sessionStorage.setItem(seenKey, '1');
      w.gtag('event', 'invite_link_clicked', {
        email: ref,
        page_location: window.location.href,
      });
    } catch (e) { /* tracking must never break the app */ }
  }

  ngOnInit() {
    this.initAnalytics();
    this.sessionTabSync.dismissModals$.subscribe(() => this.dismissSessionModals());
    this.sessionTabSync.sessionExtended$.subscribe(() => {
      this.tokenExpiryWarned = false;
    });
    this.sessionTabSync.warningClaimedElsewhere$.subscribe((type) => {
      if (type === 'token_expiry') {
        this.tokenExpiryWarned = true;
      }
    });
    this.sessionTabSync.idleReset$.subscribe(() => {
      this.userIdleService.stopTimer();
      this.userIdleService.resetTimer();
    });
    this.sessionTabSync.idleLogout$.subscribe(() => this.performSessionLogout());
    // Activity in any tab keeps every tab alive, so background tabs no longer
    // time out (and pop a warning / logout) while the user works elsewhere.
    this.sessionTabSync.activityElsewhere$.subscribe(() => {
      if (!this.authenticationService.id()) {
        return;
      }
      // Do not extend the session while a timeout warning is on screen — the
      // user must click "I'm still here" / "Stay logged in" or be logged out.
      if (this.bsUserIdleModalRef || this.bsTokenExpiryModalRef) {
        return;
      }
      this.userIdleService.stopTimer();
      this.userIdleService.resetTimer();
    });
    this.registerActivityBroadcast();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    this.startTokenExpiryWatcher();

    if (this.authenticationService.isSessionValid()) {
      this.socketService.connect();
    }
    // Start watching for user inactivity.
    this.userIdleService.startWatching();

    // Start watching when user idle is starting.
    this.userIdleService.onTimerStart().subscribe((count) => {
      if (count !== 1) {
        return;
      }
      if (!this.authenticationService.id()) {
        this.userIdleService.stopTimer();
        this.userIdleService.resetTimer();
        return;
      }
      this.sessionTabSync.requestWarning('idle').then((shouldShow) => {
        if (!shouldShow || !this.authenticationService.isSessionValid()) {
          this.userIdleService.stopTimer();
          this.userIdleService.resetTimer();
          return;
        }
        const openModals = document.querySelectorAll('.modal.in');
        if (openModals) {
          openModals.forEach((openModal) => {
            const modalHeader = openModal.getElementsByClassName('modal-header');
            if (modalHeader && modalHeader.length > 0) {
              const closeButton: any = modalHeader[0].getElementsByTagName('BUTTON');
              if (closeButton && closeButton.length > 0) {
                closeButton[0].click();
              }
            }
          });
        }
        this.bsUserIdleModalRef = this.modalService.show(UserIdleModalComponent, {
          initialState: USER_IDLE_CONFIG,
          class: 'modal-sm'
        });
        this.userIdleService.stopTimer();
      });
    });
  }

  private registerActivityBroadcast() {
    if (typeof window === 'undefined') {
      return;
    }
    // Listen outside Angular's zone so routine mouse/keyboard activity does not
    // trigger change detection on every event; the handler only writes a
    // throttled localStorage timestamp.
    this.ngZone.runOutsideAngular(() => {
      ACTIVITY_EVENTS.forEach((evt) =>
        window.addEventListener(evt, this.onLocalActivity, { capture: true, passive: true }),
      );
    });
  }
}
